import { useState } from "react";
import { Check, Loader2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useT } from "../../lib/i18n";

export function Field({ label, children }) {
  return <label className="block text-sm text-slate-600">{label}<div className="mt-1">{children}</div></label>;
}
export function Choice({ selected, onClick, title, desc, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-left rounded-xl border px-4 py-4 transition-colors ${selected ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-900">{title}</span>{selected && <Check size={16} className="text-emerald-600" />}</div>
      {desc && <div className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</div>}
    </button>
  );
}
export function Status({ ok, label, testid }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testid}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />
      <span className={ok ? "text-slate-900" : "text-slate-500"}>{label}</span>
    </div>
  );
}

export function AccountTypeStep({ data, setData }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <Choice selected={data.account_type === "solo"} onClick={() => setData({ ...data, account_type: "solo" })} title={t("Solo agent")} desc={t("Eén agenda, één inbox, jouw dossiers. Snelle setup.")} testid="ob-type-solo" />
      <Choice selected={data.account_type === "agency"} onClick={() => setData({ ...data, account_type: "agency" })} title={t("Agency / team")} desc={t("Teamleden uitnodigen, gedeelde agenda met rollen, dossiers samen beheren.")} testid="ob-type-agency" />
      {data.account_type === "agency" && <Field label={t("Naam van de agency")}><input className="pm-input w-full" value={data.agency_name || ""} onChange={(e) => setData({ ...data, agency_name: e.target.value })} placeholder="Pure Sports" data-testid="ob-agency-name" /></Field>}
    </div>
  );
}

export function AgencyStep({ detected, onDone }) {
  const t = useT();
  const [rows, setRows] = useState([{ email: "", role: "agent", agenda_permission: "viewer" }]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/onboarding/invites", { invites: rows.filter((r) => r.email.includes("@")) }); setResult(data.invites); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Status ok={detected.members > 1 || detected.pending_invites > 0} label={t("{a} lid · {b} uitnodiging(en) open", { a: detected.members, b: detected.pending_invites })} testid="ob-agency-status" />
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
          <input className="pm-input" placeholder="collega@agency.nl" value={r.email} onChange={(e) => set(i, "email", e.target.value)} data-testid={`ob-invite-email-${i}`} />
          <select className="pm-input w-28" value={r.role} onChange={(e) => set(i, "role", e.target.value)} data-testid={`ob-invite-role-${i}`}><option value="agent">Agent</option><option value="admin">Admin</option><option value="viewer">Viewer</option></select>
          <select className="pm-input w-32" value={r.agenda_permission} onChange={(e) => set(i, "agenda_permission", e.target.value)} title={t("Agenda-rechten")}><option value="viewer">{t("Agenda: inzien")}</option><option value="editor">{t("Agenda: bewerken")}</option></select>
          <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
        </div>
      ))}
      <button onClick={() => setRows((r) => [...r, { email: "", role: "agent", agenda_permission: "viewer" }])} className="text-xs font-semibold text-slate-600 flex items-center gap-1" data-testid="ob-invite-add"><Plus size={12} /> {t("Nog een collega")}</button>
      <button onClick={send} disabled={busy} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-2" data-testid="ob-invite-send">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("Uitnodigingen versturen")}</button>
      {result && <div className="text-xs text-emerald-700" data-testid="ob-invite-result">{t("{n} uitnodiging(en) aangemaakt — links staan ook in Settings → Team.", { n: result.length })}</div>}
    </div>
  );
}

export function ProfileStep({ data, setData, isAgency }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <Field label={t("Jouw naam")}><input className="pm-input w-full" value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} data-testid="ob-profile-name" /></Field>
      <Field label={t("Telefoon / WhatsApp")}><input className="pm-input w-full" value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} placeholder="+31 6 …" data-testid="ob-profile-phone" /></Field>
      {isAgency && <Field label={t("Agency-naam")}><input className="pm-input w-full" value={data.agency_name || ""} onChange={(e) => setData({ ...data, agency_name: e.target.value })} data-testid="ob-profile-agency" /></Field>}
    </div>
  );
}

export function CalendarStep({ data, setData }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <Choice selected={(data.provider || "native") === "native"} onClick={() => setData({ ...data, provider: "native" })} title={t("Playmaker-agenda gebruiken")} desc={t("Direct klaar. AI-afspraakherkenning en team-agenda werken meteen.")} testid="ob-cal-native" />
      <Choice selected={data.provider === "google"} onClick={() => setData({ ...data, provider: "google" })} title={t("Google Calendar koppelen")} desc={t("Synchronisatie volgt binnenkort — je keuze wordt onthouden, tot dan gebruik je de Playmaker-agenda.")} testid="ob-cal-google" />
      <Choice selected={data.provider === "outlook"} onClick={() => setData({ ...data, provider: "outlook" })} title={t("Outlook koppelen")} desc={t("Synchronisatie volgt binnenkort — je keuze wordt onthouden, tot dan gebruik je de Playmaker-agenda.")} testid="ob-cal-outlook" />
    </div>
  );
}

export function ChannelsStep({ detected, onConnected, data, setData }) {
  const t = useT();
  const [email, setEmail] = useState(detected.email_addr || "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [hint, setHint] = useState(null);
  const [manual, setManual] = useState(false);
  const [srv, setSrv] = useState({ imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 587 });
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState(null);
  const testWa = async () => {
    setWaBusy(true); setWaMsg(null);
    try { const { data: r } = await api.post("/comms/whatsapp/test", { to: data?.phone }); setWaMsg(r); }
    catch (e) { setWaMsg({ ok: false, message: formatApiErrorDetail(e?.response?.data?.detail) }); }
    finally { setWaBusy(false); }
  };
  const discover = async () => {
    if (!email.includes("@")) return;
    try {
      const { data } = await api.get("/comms/imap/discover", { params: { email } });
      if (data.found) {
        setHint(data); setManual(false);
        setSrv({ imap_host: data.imap_host, imap_port: data.imap_port, smtp_host: data.smtp_host, smtp_port: data.smtp_port });
      } else { setHint({ found: false }); setManual(true); setSrv({ imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 587 }); }
    } catch { setHint(null); }
  };
  const connect = async () => {
    setBusy(true); setMsg("");
    const body = { email_addr: email, password: pw };
    if (manual) Object.assign(body, { imap_host: srv.imap_host, imap_port: Number(srv.imap_port) || 993, imap_ssl: Number(srv.imap_port) !== 143, smtp_host: srv.smtp_host, smtp_port: Number(srv.smtp_port) || 587, smtp_starttls: Number(srv.smtp_port) !== 465 });
    try { await api.post("/comms/imap/connect", body); setMsg(t("E-mail verbonden.")); onConnected(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "manual_required") setManual(true);
      setMsg(formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };
  const canConnect = email && pw && (!manual || (srv.imap_host && srv.smtp_host));
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Status ok={detected.email_connected} label={detected.email_connected ? `${t("E-mail verbonden")}: ${detected.email_addr}` : t("E-mail nog niet verbonden")} testid="ob-email-status" />
        {!detected.email_connected && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <input className="pm-input" placeholder="jij@agency.nl" value={email} onChange={(e) => { setEmail(e.target.value); setHint(null); }} onBlur={discover} data-testid="ob-imap-email" />
              <input className="pm-input" type="password" placeholder={hint?.needs_app_password ? t("App-wachtwoord") : t("Wachtwoord van je mailbox")} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="ob-imap-password" />
              <button onClick={connect} disabled={busy || !canConnect} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold" data-testid="ob-imap-connect">{busy ? <Loader2 size={14} className="animate-spin" /> : t("Verbinden")}</button>
            </div>
            {hint?.found && !manual && (
              <div className="text-xs text-slate-500" data-testid="ob-imap-hint">
                {t("Gevonden")}: <span className="font-medium text-slate-700">{hint.label}</span> ({hint.imap_host}). {hint.help || ""}
                <button type="button" className="ml-1 underline" onClick={() => setManual(true)} data-testid="ob-imap-manual-toggle">{t("Servers aanpassen")}</button>
              </div>
            )}
            {manual && (
              <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="ob-imap-manual">
                <div className="text-xs text-slate-500">{t("Vul de servergegevens van je mailprovider in (staan meestal in de helppagina's van je hostingpartij).")}</div>
                <div className="grid grid-cols-[1fr_90px] gap-2">
                  <input className="pm-input" placeholder={t("IMAP-server, bv. imap.one.com")} value={srv.imap_host} onChange={(e) => setSrv({ ...srv, imap_host: e.target.value.trim() })} data-testid="ob-imap-host" />
                  <input className="pm-input" type="number" value={srv.imap_port} onChange={(e) => setSrv({ ...srv, imap_port: e.target.value })} data-testid="ob-imap-port" />
                  <input className="pm-input" placeholder={t("SMTP-server, bv. send.one.com")} value={srv.smtp_host} onChange={(e) => setSrv({ ...srv, smtp_host: e.target.value.trim() })} data-testid="ob-smtp-host" />
                  <input className="pm-input" type="number" value={srv.smtp_port} onChange={(e) => setSrv({ ...srv, smtp_port: e.target.value })} data-testid="ob-smtp-port" />
                </div>
              </div>
            )}
          </>
        )}
        {msg && <div className="text-xs text-slate-600" data-testid="ob-imap-msg">{msg}</div>}
      </div>
      <div className="space-y-2">
        <Status ok={detected.whatsapp_connected} label={detected.whatsapp_connected ? t("WhatsApp (Twilio) actief op dit platform") : t("WhatsApp (Twilio) nog niet geconfigureerd — een admin regelt dit in Settings → Integrations")} testid="ob-whatsapp-status" />
        {detected.whatsapp_connected && (
          <>
            <Field label={t("Jouw WhatsApp-nummer (voor briefings en wedstrijdmeldingen)")}>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <input className="pm-input w-full" placeholder="+31 6 …" value={data?.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} data-testid="ob-whatsapp-number" />
                <button type="button" onClick={testWa} disabled={waBusy || !(data?.phone || "").trim()} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold" data-testid="ob-whatsapp-test">{waBusy ? <Loader2 size={14} className="animate-spin" /> : t("Testbericht")}</button>
              </div>
            </Field>
            {waMsg && <div className={`text-xs ${waMsg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="ob-whatsapp-msg">{waMsg.message}</div>}
          </>
        )}
      </div>
    </div>
  );
}

export function FirstClientStep({ detected, onAdded }) {
  const t = useT();
  const [form, setForm] = useState({ full_name: "", position: "Midfielder", current_club: "", date_of_birth: "2000-01-01" });
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState([]);
  const [err, setErr] = useState("");
  const add = async () => {
    if (!form.full_name.trim()) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post("/onboarding/first-client", form); setAdded((a) => [...a, data.client]); setForm({ ...form, full_name: "", current_club: "" }); onAdded(); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Status ok={detected.clients > 0} label={t("{n} dossier(s) in je workspace", { n: detected.clients })} testid="ob-client-status" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className="pm-input sm:col-span-2" placeholder={t("Volledige naam speler")} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ob-client-name" />
        <select className="pm-input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="ob-client-position">{["Goalkeeper", "Defender", "Midfielder", "Forward"].map((p) => <option key={p}>{p}</option>)}</select>
        <input className="pm-input" placeholder={t("Huidige club")} value={form.current_club} onChange={(e) => setForm({ ...form, current_club: e.target.value })} data-testid="ob-client-club" />
        <input type="date" className="pm-input" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="ob-client-dob" />
        <button onClick={add} disabled={busy || !form.full_name.trim()} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-2" data-testid="ob-client-add">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t("Dossier toevoegen")}</button>
      </div>
      {added.length > 0 && <div className="text-xs text-emerald-700" data-testid="ob-client-added">{t("Toegevoegd")}: {added.map((c) => c.full_name).join(", ")}</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="text-xs text-slate-500">{t("Meer spelers of een bulk-import (CSV/vCard) kan later via Clients → Import.")}</div>
    </div>
  );
}

export function PreferencesStep({ data, setData }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <Field label={t("Ochtendbriefing")}>
        <div className="grid grid-cols-2 gap-2">
          <Choice selected={(data.briefing_frequency || "daily") === "daily"} onClick={() => setData({ ...data, briefing_frequency: "daily" })} title={t("Dagelijks")} testid="ob-pref-daily" />
          <Choice selected={data.briefing_frequency === "weekly"} onClick={() => setData({ ...data, briefing_frequency: "weekly" })} title={t("Wekelijks")} testid="ob-pref-weekly" />
        </div>
      </Field>
      <Field label={t("Kanaal voor briefing én wedstrijdrapport")}>
        <div className="grid grid-cols-3 gap-2">
          {[["email", "E-mail"], ["whatsapp", "WhatsApp"], ["both", "Beide"]].map(([v, l]) => <Choice key={v} selected={(data.channel || "email") === v} onClick={() => setData({ ...data, channel: v })} title={t(l)} testid={`ob-pref-${v}`} />)}
        </div>
      </Field>
      {(data.channel === "whatsapp" || data.channel === "both") && <Field label={t("WhatsApp-nummer")}><input className="pm-input w-full" placeholder="+31 6 …" value={data.whatsapp_to || ""} onChange={(e) => setData({ ...data, whatsapp_to: e.target.value })} data-testid="ob-pref-whatsapp-to" /></Field>}
      <Field label={t("Wedstrijdmelding")}>
        <select className="pm-input w-full" value={data.notify_trigger || "full_time"} onChange={(e) => setData({ ...data, notify_trigger: e.target.value })} data-testid="ob-pref-trigger"><option value="full_time">{t("Bij einde wedstrijd")}</option><option value="kickoff">{t("Bij aftrap")}</option><option value="both">{t("Aftrap én einde")}</option></select>
      </Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.match_report !== false} onChange={(e) => setData({ ...data, match_report: e.target.checked })} data-testid="ob-pref-match-report" /> {t("Weekoverzicht prestaties elke maandag")}</label>
    </div>
  );
}

export function ProgramStep({ data, setData }) {
  const t = useT();
  const goals = data.goals || ["", "", ""];
  return (
    <div className="space-y-3">
      <Field label={t("Titel")}><input className="pm-input w-full" value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder={t("Q4 — Portfolio versterken")} data-testid="ob-prog-title" /></Field>
      <Field label={t("Visie (waar sta je over 12 weken?)")}><textarea className="pm-input w-full min-h-[70px]" value={data.vision || ""} onChange={(e) => setData({ ...data, vision: e.target.value })} data-testid="ob-prog-vision" /></Field>
      <Field label={t("Drie doelen")}>
        <div className="space-y-2">{goals.map((g, i) => <input key={i} className="pm-input w-full" value={g} onChange={(e) => setData({ ...data, goals: goals.map((x, j) => (j === i ? e.target.value : x)) })} placeholder={`${t("Doel")} ${i + 1}`} data-testid={`ob-prog-goal-${i}`} />)}</div>
      </Field>
      <Field label={t("Tactiek (hoe ga je het aanpakken?)")}><textarea className="pm-input w-full min-h-[60px]" value={data.tactics || ""} onChange={(e) => setData({ ...data, tactics: e.target.value })} data-testid="ob-prog-tactics" /></Field>
      <Field label={t("Weekdoelen voor de scorecard")}>
        <div className="grid grid-cols-3 gap-2">
          {[["contacts", "Contacten"], ["appointments", "Afspraken"], ["tasks", "Taken"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-500">{t(l)}<input type="number" min="1" className="pm-input mt-1 w-full" value={(data.targets || {})[k] ?? { contacts: 15, appointments: 5, tasks: 8 }[k]} onChange={(e) => setData({ ...data, targets: { ...(data.targets || {}), [k]: e.target.value } })} data-testid={`ob-prog-target-${k}`} /></label>
          ))}
        </div>
      </Field>
    </div>
  );
}
